import { Descendant, BaseEditor, BaseRange, Range, Element } from "slate";
import { ReactEditor, RenderElementProps } from "slate-react";
import { HistoryEditor } from "slate-history";

export type BlockType =
  | "paragraph"
  | "heading-one"
  | "heading-two"
  | "heading-three"
  | "block-quote"
  | "bulleted-list"
  | "numbered-list"
  | "list-item";

export type Alignment = "left" | "center" | "right" | "justify";

export const LIST_TYPES = ["numbered-list", "bulleted-list"] as const;
export const TEXT_ALIGN_TYPES = ["left", "center", "right", "justify"] as const;
export type AlignType = (typeof TEXT_ALIGN_TYPES)[number];
export type ListType = (typeof LIST_TYPES)[number];
export type CustomElementFormat = CustomElementType | AlignType | ListType;

export type MarkFormat =
  | "bold"
  | "italic"
  | "underline"
  | "code"
  | "strikethrough"
  | "numbered-list"
  | "bulleted-list";

export type CustomElement =
  | BlockQuoteElement
  | BulletedListElement
  | EditableVoidElement
  | HeadingElement
  | HeadingTwoElement
  | HeadingThreeElement
  | LinkElement
  | ButtonElement
  | ListItemElement
  | NumberedListItemElement
  | ParagraphElement
  | TitleElement
  | CodeBlockElement
  | CodeLineElement;

export interface CustomText {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
  strikethrough?: boolean;
  // MARKDOWN PREVIEW SPECIFIC LEAF
  underlined?: boolean;
  title?: boolean;
  list?: boolean;
  hr?: boolean;
  blockquote?: boolean;
}

export interface MarkButtonProps {
  format: CustomTextKey;
}
export interface BlockButtonProps {
  format: CustomElementFormat;
}

export type CustomEditor = BaseEditor &
  ReactEditor &
  HistoryEditor & {
    nodeToDecorations?: Map<Element, Range[]>;
  };

declare module "slate" {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

export interface ElementExtendedProps {
  attributes: any;
  children: any;
  element: CustomElement;
}

export interface LeafExtendedProps {
  attributes: any;
  children: any;
  leaf: CustomText;
}

export type BlockQuoteElement = {
  type: "block-quote";
  align?: string;
  children: Descendant[];
};

export type BulletedListElement = {
  type: "bulleted-list";
  align?: string;
  children: Descendant[];
};

export type EditableVoidElement = {
  type: "editable-void";
  children: EmptyText[];
};

export type HeadingElement = {
  type: "heading-one";
  align?: string;
  children: Descendant[];
};

export type HeadingTwoElement = {
  type: "heading-two";
  align?: string;
  children: Descendant[];
};

export type HeadingThreeElement = {
  type: "heading-three";
  align?: string;
  children: Descendant[];
};

export type LinkElement = { type: "link"; url: string; children: Descendant[] };

export type ButtonElement = { type: "button"; children: Descendant[] };

export type ListItemElement = { type: "list-item"; children: Descendant[] };

export type NumberedListItemElement = {
  type: "numbered-list";
  children: Descendant[];
};

export type ParagraphElement = {
  type: "paragraph";
  align?: string;
  children: Descendant[];
};

export type TitleElement = { type: "title"; children: Descendant[] };

export type CodeBlockElement = {
  type: "code-block";
  language: string;
  children: Descendant[];
};

export type CodeLineElement = {
  type: "code-line";
  children: Descendant[];
};

export type CustomElementWithAlign =
  | ParagraphElement
  | HeadingElement
  | HeadingTwoElement
  | HeadingThreeElement
  | BlockQuoteElement
  | BulletedListElement;

export type CustomElementType = CustomElement["type"];

export type CustomTextKey = keyof Omit<CustomText, "text">;

export type EmptyText = {
  text: string;
};

export type RenderElementPropsFor<T> = RenderElementProps & {
  element: T;
};

declare module "slate" {
  interface CustomTypes {
    Editor: CustomEditor;
    Element: CustomElement;
    Text: CustomText;
    Range: BaseRange & {
      [key: string]: unknown;
    };
  }
}
